import i18n from "i18next";
/**
 * The mobile reader's model (Olivia, design-handoffs/2026-10-10-mobile-reader):
 * what a tap on the document means, and the words the reader shows for it.
 * The document itself is read-only on a phone; a tap never places a caret.
 */

export interface ReaderBlock {
  id: string;
  type: string;
  content: Record<string, unknown>;
}

/** What a tap on a block does (README, *Taps on the document*). */
export type TapAction =
  /** Paragraph, list, quote, heading: selects it as Ask Lilia's context. */
  | "select"
  /** Figure, table, display equation: opens the full-screen viewer. */
  | "view"
  /** Anything else (breaks, the title, the bibliography): nothing. */
  | "none";

const SELECTABLE = new Set(["paragraph", "heading", "list", "blockquote", "theorem", "callout", "abstract", "verse", "aside", "footnote", "algorithm", "code"]);
const VIEWABLE = new Set(["figure", "table", "equation"]);

export function tapAction(type: string): TapAction {
  if (SELECTABLE.has(type)) return "select";
  if (VIEWABLE.has(type)) return "view";
  return "none";
}

/** The block's kind in a sentence: "Ask about this paragraph". */
export function blockNoun(type: string): string {
  switch (type) {
    case "heading":
      return "heading";
    case "list":
      return "list";
    case "blockquote":
      return "quote";
    case "figure":
      return "figure";
    case "table":
      return "table";
    case "equation":
      return "equation";
    case "theorem":
      return "theorem";
    case "code":
      return "code";
    default:
      return "paragraph";
  }
}

/** The noun in the interface's language: "paragraph", "paragraphe", "párrafo". */
export function nounLabel(type: string): string {
  return i18n.t(`mobile:reader.nouns.${blockNoun(type)}`);
}

function headingLevel(b: ReaderBlock): number {
  const l = b.content.level;
  return typeof l === "number" && l >= 1 ? l : 1;
}

function numbered(b: ReaderBlock): boolean {
  return b.content.numbered !== false;
}

/**
 * Where a block is, as the context chip in Ask Lilia says it: "§2, paragraph 2",
 * "§2.1, table", "paragraph 3" before any numbered heading. Section numbers count
 * numbered headings as the PDF does; paragraphs count from the section's start.
 */
export function blockLocation(blocks: readonly ReaderBlock[], blockId: string): string | null {
  const counters: number[] = [];
  let sinceHeading = 0;
  for (const b of blocks) {
    if (b.type === "heading") {
      sinceHeading = 0;
      if (numbered(b)) {
        const level = Math.min(6, headingLevel(b));
        counters.length = level;
        counters[level - 1] = (counters[level - 1] ?? 0) + 1;
        for (let i = 0; i < level - 1; i++) counters[i] = counters[i] ?? 0;
      }
      if (b.id === blockId) {
        const what = nounLabel("heading");
        return counters.length ? i18n.t("mobile:reader.where.section", { n: counters.join("."), what }) : what;
      }
      continue;
    }
    const noun = blockNoun(b.type);
    // Only paragraphs are counted ("paragraph 2"); the title, an abstract, a callout are not.
    if (b.type === "paragraph") sinceHeading++;
    if (b.id === blockId) {
      const what = noun === "paragraph" ? i18n.t("mobile:reader.where.paragraphN", { n: sinceHeading }) : nounLabel(b.type);
      return counters.length ? i18n.t("mobile:reader.where.section", { n: counters.join("."), what }) : what;
    }
  }
  return null;
}

/** A short quote of the block's text, for the context the message carries. */
export function blockExcerpt(b: ReaderBlock, max = 140): string {
  const text = typeof b.content.text === "string" ? b.content.text : typeof b.content.caption === "string" ? b.content.caption : "";
  const flat = text.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat;
}

export interface AskContext {
  blockId: string;
  /** "§2, paragraph 2" */
  location: string;
  /** "paragraph" */
  noun: string;
  excerpt: string;
}

export function askContext(blocks: readonly ReaderBlock[], blockId: string): AskContext | null {
  const b = blocks.find((x) => x.id === blockId);
  const location = blockLocation(blocks, blockId);
  if (!b || !location) return null;
  return { blockId, location, noun: blockNoun(b.type), excerpt: blockExcerpt(b) };
}

/**
 * The message as sent, with its context: Ask Lilia scopes its edits to that
 * block unless the request says otherwise (README, *Taps*: "The block chip
 * carries its location into the sheet").
 */
export function withAskContext(message: string, ctx: AskContext | null): string {
  if (!ctx) return message;
  const quote = ctx.excerpt ? ` — "${ctx.excerpt}"` : "";
  return `[About ${ctx.location} (block ${ctx.blockId})${quote}. Change only this ${ctx.noun} unless I say otherwise.]\n${message}`;
}

// ── Reference cards (README, Taps: citation, footnote, \ref) ─────────────

export interface BibEntry {
  citeKey: string;
  fields: Record<string, string>;
}

export type RefCard =
  | { kind: "citation"; keys: string[]; entries: Array<{ key: string; text: string | null }> }
  | { kind: "footnote"; number: string | null; text: string }
  | { kind: "reference"; label: string; shown: string; targetId: string | null; title: string };

/** A bibliography entry in one line: "H. S. Carslaw and J. C. Jaeger. Conduction of Heat in Solids. 1959." */
export function bibLine(e: BibEntry): string {
  const f = e.fields;
  const clean = (s: string | undefined) => (s ?? "").replace(/[{}]/g, "").replace(/\s+/g, " ").trim();
  const parts = [clean(f.author), clean(f.title), clean(f.journal || f.booktitle || f.publisher), clean(f.year)].filter(Boolean);
  return parts.length ? `${parts.join(". ")}.` : e.citeKey;
}

const KIND_WORD: Record<string, string> = { figure: "Figure", table: "Table", equation: "Equation", heading: "Section", theorem: "Theorem", algorithm: "Algorithm", code: "Listing" };
const PREFIX: Record<string, string> = { figure: "fig", table: "tab", equation: "eq", heading: "sec", theorem: "thm", algorithm: "alg", code: "lst" };

/** The block a label points at: its own label, with or without the conventional prefix. */
export function labelTarget(blocks: readonly ReaderBlock[], key: string): ReaderBlock | null {
  for (const b of blocks) {
    const label = typeof b.content.label === "string" ? b.content.label.trim() : "";
    if (!label) continue;
    if (label === key || `${PREFIX[b.type] ?? "ref"}:${label}` === key) return b;
  }
  return null;
}

/** "Figure 1", "Equation (2.1)", "Section 3": what the card's "Go to" names. */
export function targetName(b: ReaderBlock | null, shown: string): string {
  if (!b) return shown;
  const word = KIND_WORD[b.type] ?? "Block";
  return b.type === "equation" ? `${word} ${shown.startsWith("(") ? shown : `(${shown})`}` : `${word} ${shown}`;
}

export function referenceCard(blocks: readonly ReaderBlock[], label: string, shown: string): RefCard {
  const target = labelTarget(blocks, label);
  const caption = target ? blockExcerpt(target, 200) : "";
  return { kind: "reference", label, shown, targetId: target?.id ?? null, title: caption ? `${targetName(target, shown)} · ${caption}` : targetName(target, shown) };
}

export function citationCard(bibliography: readonly BibEntry[], keyAttr: string): RefCard {
  const keys = keyAttr.split(",").map((k) => k.trim()).filter(Boolean);
  const byKey = new Map(bibliography.map((e) => [e.citeKey, e]));
  return { kind: "citation", keys, entries: keys.map((key) => ({ key, text: byKey.has(key) ? bibLine(byKey.get(key)!) : null })) };
}

// ── Documents on a phone (README 7, screen 01) ──────────────────────────

export interface DocRow {
  id: string;
  title: string;
  updatedAt: string;
  role?: string;
  ownerName?: string;
}

/** Recent first, grouped Today · This week · Earlier (days by the reader's clock). */
export function groupByRecency<T extends DocRow>(docs: readonly T[], now: Date = new Date()): Array<{ label: string; items: T[] }> {
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const weekAgo = startOfDay - 6 * 86_400_000;
  const groups: Array<{ label: string; items: T[] }> = [
    { label: i18n.t("mobile:groups.today"), items: [] },
    { label: i18n.t("mobile:groups.thisWeek"), items: [] },
    { label: i18n.t("mobile:groups.earlier"), items: [] },
  ];
  const sorted = [...docs].sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt));
  for (const d of sorted) {
    const t = Date.parse(d.updatedAt);
    groups[t >= startOfDay ? 0 : t >= weekAgo ? 1 : 2].items.push(d);
  }
  return groups.filter((g) => g.items.length > 0);
}

/** "Edited 12 min ago", "Edited 2 h ago", "Edited Tue", "Edited 3 Oct". */
export function editedWhen(updatedAt: string, now: Date = new Date()): string {
  const t = new Date(updatedAt);
  const mins = Math.max(0, Math.round((now.getTime() - t.getTime()) / 60_000));
  // In the interface's language (en-GB kept for English: 3 Oct, not Oct 3).
  const locale = i18n.language?.startsWith("fr") ? "fr-FR" : i18n.language?.startsWith("es") ? "es-ES" : "en-GB";
  if (mins < 1) return i18n.t("mobile:edited.justNow");
  if (mins < 60) return i18n.t("mobile:edited.minutes", { count: mins });
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (t.getTime() >= startOfDay) return i18n.t("mobile:edited.hours", { count: Math.round(mins / 60) });
  if (t.getTime() >= startOfDay - 6 * 86_400_000) return i18n.t("mobile:edited.on", { date: t.toLocaleDateString(locale, { weekday: "short" }) });
  return i18n.t("mobile:edited.on", { date: t.toLocaleDateString(locale, { day: "numeric", month: "short" }) });
}
